import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Check, Crown, Clock } from "lucide-react";
import SubscriptionTermsDialog, { TERMS_VERSION } from "@/components/SubscriptionTermsDialog";

const USAGE_LABELS = {
  max_users: "Usuários",
  max_active_lots: "Lotes ativos",
  max_items: "Mercadorias",
  max_monthly_sales: "Vendas no mês",
};
const STATUS_LABEL = {
  TRIALING: "Em teste", ACTIVE: "Ativa", PAST_DUE: "Pagamento pendente",
  CANCELLED: "Cancelada", EXPIRED: "Teste expirado", SUSPENDED: "Suspensa",
};

export default function PlanoAssinatura() {
  const [sub, setSub] = useState(null);
  const [usage, setUsage] = useState({});
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, p] = await Promise.all([api.get("/subscription"), api.get("/usage"), api.get("/plans")]);
      setSub(s.data); setUsage(u.data); setPlans(p.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const b = params.get("billing");
    if (b === "success") {
      toast.success("Assinatura ativada com sucesso!");
      window.history.replaceState({}, "", "/plano");
      const t = setTimeout(() => load(), 6000);
      return () => clearTimeout(t);
    }
    if (b === "cancel") {
      toast.info("Checkout cancelado.");
      window.history.replaceState({}, "", "/plano");
    }
  }, [load]);

  const openTerms = (plan) => { setPendingPlan(plan); setTermsOpen(true); };
  const checkout = async (code) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { plan_code: code, origin_url: window.location.origin, accept_terms: true, terms_version: TERMS_VERSION });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancelar a assinatura ao fim do período atual? Seus dados são preservados.")) return;
    try { await api.post("/billing/cancel"); toast.success("Cancelamento agendado"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-8" data-testid="plano-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Plano e Assinatura</h1>
        <p className="text-sm text-muted-foreground mt-1">Gerencie seu plano, acompanhe o uso e a cobrança da organização.</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 flex flex-wrap items-center justify-between gap-4" data-testid="current-sub">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display text-2xl font-bold">{sub?.plan?.name}</span>
            <Badge variant="secondary">{STATUS_LABEL[sub?.status] || sub?.status}</Badge>
          </div>
          {sub?.status === "TRIALING" && (
            <p className="text-sm text-amber-600 mt-1 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {sub.trial_days_left} dia(s) restantes de teste (até {(sub.trial_end || "").slice(0, 10)})</p>
          )}
          {sub?.status === "EXPIRED" && <p className="text-sm text-red-600 mt-1">Seu período de teste terminou. Escolha um plano para continuar criando dados.</p>}
          {sub?.cancel_at_period_end && <p className="text-sm text-muted-foreground mt-1">Cancelamento agendado para {(sub.current_period_end || "").slice(0, 10)}.</p>}
        </div>
        {sub?.status === "ACTIVE" && !sub?.cancel_at_period_end && (
          <Button variant="outline" onClick={cancel} data-testid="cancel-sub-button">Cancelar assinatura</Button>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="font-semibold mb-4">Uso do plano</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {Object.entries(usage).map(([k, v]) => (
            <div key={k} data-testid={`usage-${k}`}>
              <div className="flex justify-between text-sm mb-1">
                <span>{USAGE_LABELS[k] || k}</span>
                <span className="font-num text-muted-foreground">{v.used} de {v.limit === -1 ? "∞" : v.limit}</span>
              </div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <div className={`h-full ${v.pct >= 100 ? "bg-red-500" : v.pct >= 80 ? "bg-amber-500" : "bg-accent"}`} style={{ width: `${v.pct == null ? 3 : Math.max(3, v.pct)}%` }} />
              </div>
              {v.pct >= 80 && v.pct < 100 && <p className="text-xs text-amber-600 mt-1">Próximo do limite</p>}
              {v.pct >= 100 && <p className="text-xs text-red-600 mt-1">Limite atingido — faça upgrade</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => {
          const isCurrentTier = p.code === sub?.plan?.code;
          const isActivePlan = isCurrentTier && sub?.status === "ACTIVE";
          return (
            <div key={p.code} className={`rounded-xl border p-6 ${isActivePlan ? "border-accent ring-1 ring-accent" : "border-border"} bg-card`} data-testid={`plan-${p.code}`}>
              <div className="flex items-center justify-between">
                <span className="font-display text-xl font-bold">{p.name}</span>
                {isActivePlan && <Crown className="h-4 w-4 text-accent" />}
              </div>
              <p className="mt-2"><span className="font-num text-3xl font-extrabold">R$ {p.price.toFixed(0)}</span><span className="text-muted-foreground text-sm">/mês</span></p>
              <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500" /> {p.limits.max_users === -1 ? "Usuários ilimitados" : `${p.limits.max_users} usuários`}</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500" /> {p.limits.max_active_lots === -1 ? "Lotes ilimitados" : `${p.limits.max_active_lots} lotes ativos`}</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500" /> {p.limits.max_monthly_sales === -1 ? "Vendas ilimitadas" : `${p.limits.max_monthly_sales} vendas/mês`}</li>
                {p.limits.advanced_reports && <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500" /> Relatórios avançados</li>}
                {p.limits.data_export && <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500" /> Exportação de dados</li>}
              </ul>
              <Button className="w-full mt-5" disabled={busy || isActivePlan} onClick={() => openTerms(p)} data-testid={`choose-${p.code}`}>
                {isActivePlan ? "Plano atual" : "Assinar"}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">Pagamento processado com segurança pelo Stripe. Cartão de teste: 4242 4242 4242 4242.</p>

      <SubscriptionTermsDialog
        open={termsOpen}
        onOpenChange={setTermsOpen}
        planName={pendingPlan?.name}
        planPrice={pendingPlan?.price}
        trialDaysLeft={sub?.status === "TRIALING" ? (sub?.trial_days_left ?? 0) : 0}
        busy={busy}
        onConfirm={() => { setTermsOpen(false); checkout(pendingPlan.code); }}
      />
    </div>
  );
}
